import Decimal from 'decimal.js';
import Encoding from 'encoding-japanese';
import { DateTime } from 'luxon';
import { unparse } from 'papaparse';

import { ItemInventory } from 'apis/inventory-management-service/types';
import { ItemConfiguration, PickingAreasSearchResult } from 'apis/picker-api/types';
import { DepositPermutation } from 'apis/restaurant-api/merchant-admin/deposits/types';
import {
  Item,
  ProductHierarchyLevel,
  Translation,
  ItemCommission,
  Category,
  MandatoryWarning,
  MandatoryWarningCSVData,
  AgeRestriction,
  OTCRestriction,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { downloadEncodedCsv, isDefined } from 'common/utils';

import { ValidCSVData } from './CSVItemDetailsUploadModal/validations';
import { UNASSIGNED_AREA_ID } from './constants';
import { CSVColumnKey, PickingAreasItemSearchResult } from './types';

export const isAgeRestriction = (restriction: OTCRestriction | AgeRestriction): restriction is AgeRestriction =>
  restriction.type === 'age_restricted';

export const isOTCRestriction = (restriction: OTCRestriction | AgeRestriction): restriction is OTCRestriction =>
  restriction.type === 'over_the_counter';

export const flattenPickingAreasSearchResult = (results: PickingAreasSearchResult): PickingAreasItemSearchResult[] =>
  results.pickingAreas.flatMap(area =>
    area.items.map(item => ({ itemId: item.id, order: item.order, areaId: area.id, areaName: area.name })),
  );

const getTranslationValue = (translations: Translation[], language: Language) =>
  translations.find(t => t.lang === language)?.value || null;

const getWeightedItemSellByOption = (sellByWeightConfigInputType: 'grams' | 'number_of_items' | undefined) => {
  if (sellByWeightConfigInputType) {
    return sellByWeightConfigInputType === 'grams' ? 'weight' : 'piece';
  }
  return null;
};

const mandatoryWarningToCsvDataRecord: Record<MandatoryWarning, MandatoryWarningCSVData> = {
  'with-alcohol-soft': 'alcohol_soft',
  'with-alcohol-hard': 'alcohol_hard',
  'choking-sausages': 'choking_sausages',
  'choking-nuts': 'choking_nuts',
  'with-sugar': 'sugar',
  'with-fat': 'fat',
  'with-sodium': 'sodium',
  'medicine-otc': 'otc',
};

const itemToCsvTransformerFns: Record<
  CSVColumnKey,
  (
    item: Item,
    settings: {
      categories: Category[];
      language: Language;
      inventory: ItemInventory[];
      productHierarchyLevels: ProductHierarchyLevel[];
      itemConfigs: ItemConfiguration[];
      itemCommissions: ItemCommission[];
      pickingAreasItemSearchResult: PickingAreasItemSearchResult[];
      deposits?: DepositPermutation[];
    },
    convertNumberStringsToNumbers?: boolean,
  ) => string | number | null
> = {
  age_limit: item => item.restrictions.find(isAgeRestriction)?.age_limit || null,
  item_id: item => item.id,
  gtin: item => item.barcode_gtin,
  merchant_sku: item => item.merchant_sku,
  name: (item, { language }) => getTranslationValue(item.name, language),
  brand_id: item => item.brand_id,
  caffeine_content_units: item => item.caffeine_content?.units || null,
  caffeine_content_value: item => item.caffeine_content?.value || null,
  description: (item, { language }) => getTranslationValue(item.description, language),
  dietary_preferences: item => item.dietary_preferences.join(' '),
  mandatory_warnings: item =>
    item.mandatory_warnings.map(mandatoryWarning => mandatoryWarningToCsvDataRecord[mandatoryWarning]).join(' '),
  image_url: item => item.image,
  product_hierarchy_id: (item, { productHierarchyLevels }) =>
    productHierarchyLevels.find(l => l.id === item.product_hierarchy_level_id)?.human_readable_id || null,
  product_hierarchy_name: (item, { productHierarchyLevels, language }) => {
    const productHierarchyLevel =
      productHierarchyLevels.find(l => l.id === item.product_hierarchy_level_id)?.name ?? [];
    return getTranslationValue(productHierarchyLevel, language) ?? getTranslationValue(productHierarchyLevel, 'en');
  },
  price: (item, _, mapNumberStringsToNumbers) => {
    if (mapNumberStringsToNumbers) {
      if (item.discounted) {
        return Number(item.discounted.original_price.amount);
      }
      return Number(item.baseprice.amount);
    }
    return item.discounted?.original_price.amount || item.baseprice.amount;
  },
  discounted_price: (item, _, mapNumberStringsToNumbers) => {
    if (mapNumberStringsToNumbers) {
      if (item.discounted) {
        return Number(item.baseprice.amount);
      }
      return null;
    }
    return item.discounted ? item.baseprice.amount : null;
  },
  weight_in_grams: item => (item.sell_by_weight_config ? null : item.weight_in_grams),
  volume_in_ml: item => (item.sell_by_weight_config ? null : item.volume_in_ml),
  number_of_units: item => (item.sell_by_weight_config ? null : item.number_of_units),
  display_unit: item => item.display_unit,
  weighted_item_sell_by: item => getWeightedItemSellByOption(item.sell_by_weight_config?.input_type),
  weighted_item_approx_weight_in_grams: item =>
    (item.sell_by_weight_config?.input_type === 'number_of_items' && item.sell_by_weight_config?.grams_per_step) ||
    null,
  weighted_item_min_weight_in_grams: item =>
    (item.sell_by_weight_config?.input_type === 'grams' && item.sell_by_weight_config?.grams_per_step) || null,
  vat_percentage: (item, _, mapNumberStringsToNumbers) => {
    if (mapNumberStringsToNumbers) {
      return Number(item.vat_percentage);
    }
    return item.vat_percentage;
  },
  alcohol_percentage: item => new Decimal(item.alcohol_percentage).dividedBy(10).toNumber(),
  pos_id: item => item.external_id,
  country_of_origin: (item, { language }) => getTranslationValue(item.country_of_origin, language),
  ingredients: (item, { language }) => getTranslationValue(item.ingredients, language),
  additives: (item, { language }) => getTranslationValue(item.additives, language),
  nutrition_facts: (item, { language }) => getTranslationValue(item.nutrition_facts, language),
  allergens: (item, { language }) => getTranslationValue(item.allergens, language),
  conditions_of_use_and_storage: (item, { language }) =>
    getTranslationValue(item.conditions_of_use_and_storage, language),
  more_information: (item, { language }) => getTranslationValue(item.more_information, language),
  distributor_information: (item, { language }) => getTranslationValue(item.distributor_information, language),
  deposit_value: (item, { deposits }) =>
    deposits?.find(d => d.id.$oid === item.deposit_permutation_id?.$oid)?.amount.amount ?? null,
  deposit_vat: (item, { deposits }) =>
    deposits?.find(d => d.id.$oid === item.deposit_permutation_id?.$oid)?.vat_percentage ?? null,
  producer_information: (item, { language }) => getTranslationValue(item.producer_information, language),
  enabled: item => (item.enabled ? 'YES' : 'NO'),
  is_over_the_counter: item => (item.restrictions.some(r => r.type === 'over_the_counter') ? 'YES' : 'NO'),
  min_quantity_per_purchase: item => item.min_quantity_per_purchase,
  max_quantity_per_purchase: item => item.max_quantity_per_purchase,
  use_limited_quantity: item => (item.inventory_mode === 'limited_quantity' ? 'YES' : 'NO'),
  use_inventory_management: item => (item.inventory_mode === 'inventory' ? 'YES' : 'NO'),
  is_in_stock: item => (item.inventory_mode === null || item.inventory_mode !== 'forced_out_of_stock' ? null : 'NO'),
  is_age_restricted_item: item => (item.restrictions.some(r => r.type === 'age_restricted') ? 'YES' : 'NO'),
  delivery_handshake: item => (item.restrictions.some(r => r.type === 'delivery_handshake') ? 'YES' : 'NO'),
  item_balance: (item, { inventory }) => {
    const itemInventory = inventory.find(i => i.itemId === item.id);
    const isBinaryInventory = !item.inventory_mode || item.inventory_mode === 'forced_out_of_stock';
    return !isBinaryInventory && itemInventory ? itemInventory.balance : null;
  },
  location: (item, { itemConfigs }) =>
    itemConfigs.find(itemConfig => itemConfig.itemId === item.id)?.storageLocation ?? null,
  commission_delivery: (item, { itemCommissions }) => {
    const itemCommission = itemCommissions.find(p => p.item_id === item.id && p.delivery_method === 'homedelivery');
    return itemCommission ? new Decimal(itemCommission.value).times(100).toNumber() : null;
  },
  commission_pickup: (item, { itemCommissions }) => {
    const itemCommission = itemCommissions.find(p => p.item_id === item.id && p.delivery_method === 'takeaway');
    return itemCommission ? new Decimal(itemCommission.value).times(100).toNumber() : null;
  },
  category_id: (item, { categories }) =>
    categories.find(category => category.item_bindings.map(binding => binding.item_id).includes(item.id))?.id || null,
  category_name: (item, { categories, language }) =>
    getTranslationValue(
      categories.find(category => category.item_bindings.map(binding => binding.item_id).includes(item.id))?.name || [],
      language,
    ),
  category_item_sequence: (item, { categories }) => {
    const category = categories.find(cat => cat.item_bindings.find(binding => binding.item_id === item.id));
    if (!category) {
      return null;
    }
    return category.item_bindings.findIndex(binding => binding.item_id === item.id) + 1;
  },
  picking_area: (item, { pickingAreasItemSearchResult }) => {
    const area = pickingAreasItemSearchResult.find(areaItem => areaItem.itemId === item.id);
    return !area || area.areaId === UNASSIGNED_AREA_ID ? null : area.areaName;
  },
  picking_sequence: (item, { pickingAreasItemSearchResult }) =>
    pickingAreasItemSearchResult.find(areaItem => areaItem.itemId === item.id)?.order || null,
  return_policy: item => item.return_policy ?? null,
  delivery_methods: item => item.delivery_methods.join(' '),
};

export const downloadTransformedDataAsCsv = (
  filename: string,
  language: Language,
  transformedData: Record<string, string | number | null>[],
) => {
  const parsedData = unparse(transformedData);
  downloadEncodedCsv(parsedData, 'text/csv', filename, language === 'ja');
};

export const downloadItemsAsCsv = (
  items: Item[],
  selectedColumns: CSVColumnKey[],
  filename: string,
  settings: {
    categories: Category[];
    language: Language;
    inventory: ItemInventory[];
    productHierarchyLevels: ProductHierarchyLevel[];
    itemConfigs: ItemConfiguration[];
    itemCommissions: ItemCommission[];
    pickingAreasItemSearchResult: PickingAreasItemSearchResult[];
    deposits?: DepositPermutation[];
    errorData?: { [itemId: string]: (string | undefined)[] };
  },
) => {
  const transformedData = items.map(item => {
    const errors = settings.errorData && settings.errorData[item.id]?.filter(isDefined).join('\n');
    const csvData = selectedColumns.reduce<Record<string, string | number | null>>((acc, currCol) => {
      const val = itemToCsvTransformerFns[currCol](item, settings);
      return {
        ...acc,
        [currCol]: currCol === 'gtin' && !!val ? `="${val}"` : val,
      };
    }, {});
    return errors ? { ...csvData, errors } : csvData;
  });
  const parsedData = unparse(transformedData);
  downloadEncodedCsv(parsedData, 'text/csv', filename, settings.language === 'ja');
};

export const mapSelectedFields = (
  items: Item[],
  selectedColumns: CSVColumnKey[],
  settings: {
    categories: Category[];
    language: Language;
    inventory: ItemInventory[];
    productHierarchyLevels: ProductHierarchyLevel[];
    itemConfigs: ItemConfiguration[];
    itemCommissions: ItemCommission[];
    pickingAreasItemSearchResult: PickingAreasItemSearchResult[];
    deposits?: DepositPermutation[];
    errorData?: { [itemId: string]: (string | undefined)[] };
  },
  convertNumberStringsToNumbers?: boolean,
) => {
  return items.map(item => {
    const errors = settings.errorData && settings.errorData[item.id]?.filter(isDefined).join('\n');
    const csvData = selectedColumns.reduce<Record<string, string | number | null>>((acc, currCol) => {
      const val = itemToCsvTransformerFns[currCol](item, settings, convertNumberStringsToNumbers);
      return { ...acc, [currCol]: val };
    }, {});
    return errors ? { ...csvData, errors } : csvData;
  });
};

export const downloadUnmatchedDataAsCsv = (
  unmatchedData: ValidCSVData[],
  selectedColumns: CSVColumnKey[],
  venueName: string,
  isJapanese: boolean,
) => {
  const sortedColumnsData = unmatchedData.map(record =>
    selectedColumns.reduce<Record<string, string | number | null | undefined>>(
      (acc, currCol) => ({
        ...acc,
        [currCol]: record[currCol],
      }),
      {},
    ),
  );
  const parsedData = unparse(sortedColumnsData);
  const filename = `${venueName}-not-found-item-details-${DateTime.local().toISODate()}.csv`;
  downloadEncodedCsv(parsedData, 'text/csv', filename, isJapanese);
};

/** Tries to parse delimiter from the header only in opposition to `papaparse` that tries from the first 10 lines */
export const parseDelimiter = (file: File): Promise<string | undefined> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const [firstLine] = reader.result.split('\n');
        const commaSeparatedLength = firstLine.split(',').length;
        const semicolonSeparatedLength = firstLine.split(';').length;
        if (commaSeparatedLength >= semicolonSeparatedLength) {
          resolve(',');
        }
        resolve(';');
      }
      resolve(undefined);
    };
    reader.onerror = () => {
      reject();
    };
    reader.readAsText(file);
  });
};

/** Convert file to UTF-8 when originally encoded in Shift-JIS */
export const normalizeEncoding = (file: File): Promise<string | File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        const readerArray = new Uint8Array(reader.result);
        const encoding = Encoding.detect(readerArray);
        if (encoding === 'SJIS') {
          const result = Encoding.convert(readerArray, { from: 'SJIS', to: 'UTF8' });
          const blobPart = new Uint8Array(result);
          resolve(new File([blobPart], file.name));
        }
      }
      resolve(file);
    };
    reader.onerror = () => {
      reject('There was an error when trying to normalize the file encoding!');
    };
    reader.readAsArrayBuffer(file);
  });
};
